import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import CustomerPortalHeader from '../../components/ui/CustomerPortalHeader';
import QuoteHeader from './components/QuoteHeader';
import CustomerInfoCard from './components/CustomerInfoCard';
import QuoteItemsTable from './components/QuoteItemsTable';
import QuoteSummary from './components/QuoteSummary';
import QuoteNotes from './components/QuoteNotes';
import ActionButtons from './components/ActionButtons';
import { Quote, ApprovalMetrics } from './types';
import { quoteService } from '../../services/quoteService';
import { emailService } from '../../services/emailService';
import Icon from '../../components/AppIcon';

// UUID validation regex - validates proper UUID format
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const generateQuotePDF = async (quote: Quote) => {
  // PDF generation logic - placeholder implementation
  console.log('Generating PDF for quote:', quote.referenceCode);
  // In a real implementation, this would call a PDF generation service
  return Promise.resolve();
};

const CustomerQuoteReviewPortal = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const quoteId = id || searchParams.get('id');

  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [metrics, setMetrics] = useState<ApprovalMetrics>({
    viewedAt: new Date().toISOString(),
    timeSpent: 0,
    ipAddress: '192.168.1.1',
  });

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      setMetrics((prev) => ({
        ...prev,
        timeSpent: Math.floor((Date.now() - startTime) / 1000),
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadQuoteData = async () => {
      // Validate quote ID exists
      if (!quoteId) {
        setError('No quote ID provided in URL');
        setLoading(false);
        return;
      }

      // Validate UUID format before attempting database query
      if (!UUID_REGEX.test(quoteId)) {
        console.error('Invalid quote ID format received. The URL may contain an unresolved route parameter.');
        setError('This quote link is invalid or incomplete. Please use the link provided in your quote email.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const quoteWithData = await quoteService.getByIdWithCustomer(quoteId);
        
        if (!quoteWithData) {
          setError('Quote not found');
          setLoading(false);
          return;
        }

        const { quote: dbQuote, lines, customer } = quoteWithData;

        // Convert database format to UI format
        const licenseItems = lines
          .filter(line => line.line_type === 'LICENSE')
          .map(line => ({
            id: line.id,
            group: line.group_name || '',
            productName: line.product_name,
            unitPrice: Number(line.unit_price || 0),
            quantity: line.quantity || 0,
            monthlyAmount: Number(line.monthly_amount || 0),
            annualAmount: Number(line.annual_amount || 0),
          }));

        const implementationItems = lines
          .filter(line => line.line_type === 'IMPLEMENTATION')
          .map(line => ({
            id: line.id,
            group: line.group_name || '',
            productName: line.product_name,
            unitPrice: Number(line.hourly_rate || 0),
            quantity: Number(line.hours || 0),
            monthlyAmount: 0,
            annualAmount: Number(line.total_amount || 0),
          }));

        const licensesMonthly = Number(dbQuote.licenses_monthly_total || 0);
        const licensesAnnual = Number(dbQuote.licenses_annual_total || 0);
        const implementationTotal = Number(dbQuote.implementation_total || 0);

        // Calculate On Demand Services and Project Overhead from stored rates
        const safeOnDemandRate = Number(dbQuote.on_demand_rate) || 0;
        const safeProjectMgmtRate = Number(dbQuote.project_mgmt_rate) || 0;

        const totalHours = lines
          .filter(line => line.line_type === 'IMPLEMENTATION')
          .reduce((sum, line) => sum + (Number(line.hours) || 0), 0);

        const onDemandHours = totalHours * 0.15;
        const onDemandTotal = onDemandHours * safeOnDemandRate;

        const projectMgmtHours = (totalHours + onDemandHours) * 0.15;
        const projectMgmtTotal = projectMgmtHours * safeProjectMgmtRate;

        const formattedQuote: Quote = {
          id: dbQuote.id,
          referenceCode: dbQuote.reference_code,
          customerName: customer?.name || dbQuote.customer_name,
          projectName: dbQuote.project_name,
          status: dbQuote.status,
          createdDate: dbQuote.quote_date,
          validUntil: dbQuote.valid_until,
          customerInfo: {
            name: customer?.name || dbQuote.customer_name,
            company: customer?.company || '',
            email: customer?.email || '',
            phone: customer?.phone || '',
            address: customer 
              ? `${customer.address || ''}, ${customer.city || ''}, ${customer.postal_code || ''}, ${customer.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
              : '',
          },
          licenses: {
            included: dbQuote.licenses_included,
            items: licenseItems,
            monthlySubtotal: licensesMonthly,
            annualSubtotal: licensesAnnual,
          },
          implementation: {
            included: dbQuote.implementation_included,
            items: implementationItems,
            monthlySubtotal: 0,
            annualSubtotal: implementationTotal,
          },
          onDemandTotal,
          projectMgmtTotal,
          notes: dbQuote.notes || '',
          totalMonthly: licensesMonthly,
          totalAnnual: licensesAnnual + implementationTotal,
          totalExVAT: Number(dbQuote.total_ex_vat || 0),
        };

        setQuote(formattedQuote);
      } catch (err) {
        console.error('Error loading quote:', err);
        setError(err instanceof Error ? err.message : 'Failed to load quote data');
      } finally {
        setLoading(false);
      }
    };

    loadQuoteData();
  }, [quoteId]);

  const handleAcceptQuote = async () => {
    if (!quote || !quoteId || quote.status === 'APPROVED') {
      console.log('⚠️ handleAcceptQuote skipped:', { 
        hasQuote: !!quote, 
        quoteId, 
        status: quote?.status 
      });
      return;
    }

    console.log('🚀 Starting quote approval process for:', quoteId);
    setIsProcessing(true);

    try {
      // Update quote status to APPROVED - this is the primary action
      console.log('📝 Updating quote status to APPROVED...');
      await quoteService.updateStatus(quoteId, 'APPROVED');
      console.log('✅ Quote status updated successfully');

      // Update local state immediately to show success
      setQuote((prev) => prev ? {
        ...prev,
        status: 'APPROVED',
      } : null);

      setMetrics((prev) => ({
        ...prev,
        approvedAt: new Date().toISOString(),
      }));

      // Try to send confirmation email, but don't block success if it fails
      // Only attempt if customer email exists
      if (quote.customerInfo.email) {
        console.log('📧 Attempting to send confirmation emails...');
        console.log('Email parameters:', {
          quoteId,
          customerEmail: quote.customerInfo.email,
          quoteReference: quote.referenceCode,
          hasLicenses: !!quote.licenses,
          hasImplementation: !!quote.implementation,
        });
        
        try {
          const emailResult = await emailService.sendQuoteAcceptanceEmail({
            quoteId: quoteId,
            customerName: quote.customerInfo.name,
            companyName: quote.customerInfo.company || quote.customerInfo.name,
            quoteReference: quote.referenceCode,
            projectName: quote.projectName,
            quoteTotal: `€${quote.totalExVAT.toFixed(2)}`,
            customerEmail: quote.customerInfo.email,
            quoteLink: `${window.location.origin}/customer-quote-review-portal/${quoteId}`,
            licenses: quote.licenses,
            implementation: quote.implementation,
          });
          
          console.log('📧 Email service result:', emailResult);
          
          if (emailResult.success) {
            console.log('✅ Confirmation emails sent successfully');
          } else {
            console.warn('⚠️ Email sending returned false success:', emailResult.error);
          }
        } catch (emailError) {
          // Log email error but don't show it to user - quote is still accepted
          console.error('💥 Email error (quote still accepted):', emailError);
          console.error('Email error details:', {
            message: emailError instanceof Error ? emailError.message : 'Unknown error',
            stack: emailError instanceof Error ? emailError.stack : undefined,
          });
        }
      } else {
        console.warn('⚠️ No customer email available, skipping confirmation emails');
      }

      // Clear any previous errors since approval succeeded
      setError(null);
      console.log('🎉 Quote approval process completed successfully');

    } catch (error) {
      console.error('💥 CRITICAL ERROR approving quote:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
      setError('Er is een fout opgetreden bij het goedkeuren van de offerte. Probeer het opnieuw.');
      
      // Revert status if approval failed
      if (quote) {
        setQuote((prev) => prev ? {
          ...prev,
          status: quote.status, // Revert to original status
        } : null);
      }
    } finally {
      setIsProcessing(false);
      console.log('🏁 handleAcceptQuote process finished');
    }
  };

  const handleDownloadPDF = async () => {
    if (!quote) return;

    try {
      setIsProcessing(true);
      await generateQuotePDF(quote);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading quote details...</p>
        </div>
      </div>
    );
  }

  // Error state UI with better messaging
  if (error || !quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h2 className="text-xl font-bold text-text-primary mb-2">
            {error?.includes('invalid or incomplete') || error?.includes('Invalid quote ID') ? 'Invalid Quote Link' : 'Error Loading Quote'}
          </h2>
          <p className="text-text-secondary mb-4">
            {error || 'Quote not found'}
          </p>
          {(error?.includes('invalid or incomplete') || error?.includes('Invalid quote ID')) && (
            <p className="text-sm text-text-secondary mb-4">
              Please use the link provided in your quote email, or contact the sender for a valid quote link.
            </p>
          )}
          <button
            onClick={() => window.location.href = '/quotes-overview-dashboard'}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return to Quotes Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CustomerPortalHeader
        quoteNumber={quote.referenceCode}
        customerName={quote.customerInfo.name}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <QuoteHeader quote={quote} />
            <CustomerInfoCard customerInfo={quote.customerInfo} />
            <QuoteItemsTable
              title="Software Licenses"
              items={quote.licenses.items}
              included={quote.licenses.included}
              onDemandTotal={0}
              projectMgmtTotal={0}
            />
            <QuoteItemsTable
              title="Implementation Services"
              items={quote.implementation.items}
              included={quote.implementation.included}
              hideMonthlyAnnual={true}
              onDemandTotal={quote.onDemandTotal}
              projectMgmtTotal={quote.projectMgmtTotal}
            />
            <QuoteNotes notes={quote.notes} />
          </div>

          <div className="lg:col-span-1">
            <div className="space-y-6">
              <QuoteSummary quote={quote} />
              <ActionButtons
                onAccept={handleAcceptQuote}
                isApproved={quote.status === 'APPROVED'}
                isProcessing={isProcessing}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerQuoteReviewPortal;